import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, ArrowLeft, X, Activity, GitBranch, TrendingUp, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";

const TOUR_FLAG = "connexis_welcome_seen";

export default function WelcomeTour({ user }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isWelcome = params.get("welcome") === "1";
    const alreadySeen = localStorage.getItem(TOUR_FLAG) === "1";
    if (isWelcome && !alreadySeen) {
      setOpen(true);
      api.get("/client/leads?limit=1").then((r) => setLatest(r.data?.[0])).catch(() => {});
    }
  }, []);

  const finish = () => {
    localStorage.setItem(TOUR_FLAG, "1");
    setOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("welcome");
    window.history.replaceState({}, "", url.toString());
  };

  if (!open) return null;

  const lead = latest || {};
  const intent = lead.intent || "your captured enquiry";
  const sector = (lead.sector || "mortgages").replace(/-/g, " ");
  const price = (lead.price || 0).toFixed(2);
  const city = lead.city || "the UK";
  const confidence = lead.confidence || 90;

  const steps = [
    {
      Icon: Activity,
      kicker: "Step 1 of 3",
      title: `Your enquiry is now live, ${(user?.name || "").split(" ")[0] || "there"}.`,
      body: (
        <>
          <p>The AI-qualified enquiry you just had is now sitting in your <strong>Live lead feed</strong> — the same socket that ingests production leads at <span className="font-mono">98.6ms</span> median.</p>
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Just landed</div>
            <div className="mt-1.5 text-sm font-medium text-slate-900">"{intent}"</div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
              <span>Sector · <span className="capitalize text-slate-900 font-medium">{sector}</span></span>
              <span>City · <span className="text-slate-900 font-medium">{city}</span></span>
              <span>Value · <span className="font-mono text-slate-900 font-semibold">£{price}</span></span>
            </div>
          </div>
        </>
      ),
    },
    {
      Icon: GitBranch,
      kicker: "Step 2 of 3",
      title: "Here's how it routed.",
      body: (
        <>
          <p>In production, every enquiry runs through a <strong>ping-tree</strong> — a multi-buyer waterfall that picks the highest-paying buyer in milliseconds.</p>
          <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <svg viewBox="0 0 360 140" className="w-full h-32">
              <defs>
                <linearGradient id="tline" x1="0" x2="1">
                  <stop offset="0%" stopColor="#0055FF" stopOpacity="0" />
                  <stop offset="50%" stopColor="#0055FF" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <rect x="6" y="58" width="88" height="28" rx="6" fill="#0F172A" />
              <text x="50" y="76" textAnchor="middle" fill="#fff" fontSize="10" fontFamily="Instrument Sans">Your lead</text>
              {[
                { y: 14, name: "Aspect Capital", win: true, p: `£${price}` },
                { y: 50, name: "Beacon Finance", win: false, p: `£${(price * 0.92).toFixed(0)}` },
                { y: 86, name: "Pillar Life", win: false, p: `£${(price * 0.84).toFixed(0)}` },
              ].map((b, i) => (
                <g key={i}>
                  <path d={`M94 72 C 170 72, 200 ${b.y + 14}, 248 ${b.y + 14}`} stroke="url(#tline)" strokeWidth="1.5" fill="none" className="flow-line" style={{ animationDelay: `${i * 0.4}s` }} />
                  <rect x="248" y={b.y} width="106" height="28" rx="6" fill={b.win ? "#0055FF" : "#fff"} stroke={b.win ? "#0055FF" : "#E2E8F0"} />
                  <text x="258" y={b.y + 18} fontSize="10" fill={b.win ? "#fff" : "#0F172A"} fontFamily="Instrument Sans">{b.name}</text>
                  <text x="346" y={b.y + 18} fontSize="10" fontWeight="600" fill={b.win ? "#fff" : "#0055FF"} textAnchor="end" fontFamily="JetBrains Mono">{b.p}</text>
                </g>
              ))}
            </svg>
            <div className="mt-2 text-xs text-slate-500 text-center">Routed at <span className="font-mono text-slate-700">{confidence}% confidence</span> · sub-second</div>
          </div>
        </>
      ),
    },
    {
      Icon: TrendingUp,
      kicker: "Step 3 of 3",
      title: "From 1 enquiry to 1,000.",
      body: (
        <>
          <p>This is what scaling on Connexis looks like. Same routing, same compliance, same dashboards — across all your sectors.</p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { l: "Today", v: "1", sub: "your lead" },
              { l: "Week 1", v: "180", sub: "first campaign" },
              { l: "Month 3", v: "4,200", sub: "multi-buyer" },
            ].map((m, i) => (
              <div key={i} className={`rounded-xl border p-3 ${i === 2 ? "border-primary bg-primary/[0.04]" : "border-slate-200 bg-white"}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{m.l}</div>
                <div className="mt-1 text-2xl tracking-tight font-semibold text-slate-900 tabular-nums">{m.v}</div>
                <div className="text-[10px] text-slate-500">{m.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3">
            <span className="inline-grid place-items-center size-9 rounded-md bg-slate-900 text-white shrink-0"><CalendarCheck className="size-4" strokeWidth={1.6} /></span>
            <div>
              <div className="text-sm font-medium text-slate-900">Want a 30-minute scale plan?</div>
              <div className="text-xs text-slate-600 mt-0.5">We'll map your sector economics, routing topology and AI surface area — bring your numbers, leave with a launch plan.</div>
            </div>
          </div>
        </>
      ),
    },
  ];

  const cur = steps[step];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-up" data-testid="welcome-tour">
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-float overflow-hidden border border-slate-200">
        <button onClick={finish} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 z-10" data-testid="welcome-skip" aria-label="Skip tour">
          <X className="size-4" />
        </button>

        <div className="px-8 pt-8 pb-6">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="size-4" />
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase">{cur.kicker}</span>
          </div>
          <div className="mt-3 flex items-start gap-3">
            <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white shrink-0"><cur.Icon className="size-5" strokeWidth={1.6} /></span>
            <h2 className="text-2xl tracking-[-0.02em] font-semibold text-slate-900 leading-tight">{cur.title}</h2>
          </div>
          <div className="mt-5 text-sm text-slate-600 leading-relaxed">{cur.body}</div>
        </div>

        <div className="px-8 py-5 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/60" : "w-1.5 bg-slate-300"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} className="rounded-full" data-testid="welcome-back">
                <ArrowLeft className="size-4" /> Back
              </Button>
            )}
            {step < steps.length - 1 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} className="rounded-full px-4" data-testid="welcome-next">
                Next <ArrowRight className="size-4" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={finish} className="rounded-full" data-testid="welcome-finish">Got it</Button>
                <Button size="sm" onClick={() => { finish(); nav("/book-demo"); }} className="rounded-full px-4" data-testid="welcome-book">
                  Book scale call <ArrowRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
